import { defineStore } from 'pinia';
import { useDsaReportsStore } from './dsaReportsStore';
import { useDsaReportsTicketsStore } from './dsaReportsTicketsStore';
import { useDsaReportsPeriodsStore } from './dsaReportsPeriodsStore';
import {
  getAuthStatus,
  getLoginUrl,
  logout as logoutRequest
} from '../features/auth/api/authApi';

export const useAuthStore = defineStore('authStore', {
  state: () => ({
    username: '',
    email: '',
    isAuthenticated: false,
    token: null,
  }),
  actions: {
    setToken(token) {
      this.token = token;
      this.isAuthenticated = !!token;  // Sets isAuthenticated based on token presence
      if (!this.isAuthenticated) {
        // Trigger other stores to clear their data when the user logs out
        this.clearOtherStores();
      }
    },
    clearOtherStores() {
      const ticketStore = useDsaReportsTicketsStore();
      const myStore = useDsaReportsStore();
      const periodStore = useDsaReportsPeriodsStore();
  
      ticketStore.clearTickets();
      myStore.clearData();
      periodStore.clearPeriods();
    },
    async checkAuthStatus() {
      try {
        const response = await getAuthStatus();
        const { token } = response.data;
        this.setToken(token);
        ////console.log("Auth status response:", response.data);
      } catch (error) {
        console.error("Error checking authentication status", error);
        this.setToken(null); // This also sets isAuthenticated to false
      }
    },
    login() {
      // Directly navigate to the OAuth login endpoint
      window.location.href = getLoginUrl();
    },
    logout() {
      const myStore = useDsaReportsStore();
      myStore.isLoading = true;
      const clearEmployeeInsightsCache = () => {
        try {
          window.localStorage.removeItem('employeeInsightsDateRange');
          window.localStorage.removeItem('employeeInsightsViewMode');
          window.localStorage.removeItem('weeklyHourTargets');
        } catch (err) {
          console.warn('Failed to clear employee insights cache:', err);
        }
      };
      const clearDsaCache = () => {
        try {
          window.localStorage.removeItem('dsaContractsCache');
          window.localStorage.removeItem('dsaCompaniesCache');
          const keysToRemove = [];
          for (let i = 0; i < window.localStorage.length; i += 1) {
            const key = window.localStorage.key(i);
            if (!key) continue;
            if (key.startsWith('dsaIssuesCache:')) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach((key) => window.localStorage.removeItem(key));
        } catch (err) {
          console.warn('Failed to clear DSA cache:', err);
        }
      };
      const clearProjectManagementCache = () => {
        try {
          const keysToRemove = [];
          for (let i = 0; i < window.localStorage.length; i += 1) {
            const key = window.localStorage.key(i);
            if (!key) continue;
            if (
              key.startsWith('pmProjectsCache:') ||
              key.startsWith('pmMilestonesCache:') ||
              key.startsWith('pmTasksJobCache:') ||
              key.startsWith('pmTasksMilestoneCache:')
            ) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach((key) => window.localStorage.removeItem(key));
        } catch (err) {
          console.warn('Failed to clear project management cache:', err);
        }
      };
      logoutRequest()
        .then(() => {
          // After successfully logging out, reset the authentication state
          this.setToken(null);  // Reset both token and isAuthenticated
          clearEmployeeInsightsCache();
          clearDsaCache();
          clearProjectManagementCache();
          // Optionally, redirect the user to a public page
          window.location.href = '/';
        })
        .catch(error => {
          console.error("Error during logout", error);
          clearEmployeeInsightsCache();
          clearDsaCache();
          clearProjectManagementCache();
          myStore.isLoading = false;
          this.setToken(null);  // Reset both token and isAuthenticated on error as well
        });
    },
  }
});
